// main.ts
import { CommonModule } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  enableProdMode,
  EnvironmentProviders,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideCheckNoChangesConfig,
  provideZonelessChangeDetection,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatMenuModule } from '@angular/material/menu';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { routes } from './app/app.routes';
import { AppComponent } from './app/app.component';
import { HomePageModule } from './app/home-page/home-page.module';
import { Auth0Module } from './app/modules/auth0/auth0.module';
import { SettingsPageModule } from './app/modules/settings-page/settings-page.module';
import { SharedModule } from './app/shared/shared.module';
import { appTooltipDefaults } from './app/shared/utils/tooltip-defaults.utils';
import { installZonelessAuditConsoleFilter } from './app/shared/utils/zoneless-audit-console-filter';
import {
  AppService,
  AssayService,
  CancerTypesService,
  EnrichmentKitsService,
  EthnicitiesService,
  PanelsService,
  PhenotypesService,
  PredictionOptionsService,
  VariantEvidencesSelectionService,
} from './app/store';
import { ClassificationMetadataService } from './app/store/services/classification-metadata/classification-metadata.service';
import { ZonelessDialogActivationService } from './app/shared/services/zoneless-dialog-activation.service';
import { environment } from './environments/environment';

import { registerLicense } from '@syncfusion/ej2-base';

registerLicense(environment.syncfusionActivationKey);

if (environment.production) {
  enableProdMode();
}

const zonelessAuditProviders: EnvironmentProviders[] = environment.zonelessAudit
  ? [provideCheckNoChangesConfig({ exhaustive: true, interval: 1000 })]
  : [];

if (environment.zonelessAudit) {
  installZonelessAuditConsoleFilter();
}

const bootstrap = () =>
  bootstrapApplication(AppComponent, {
    providers: [
      provideZonelessChangeDetection(),
      ...zonelessAuditProviders,
      provideAppInitializer(() => inject(ZonelessDialogActivationService).init()),
      provideRouter(routes),
      importProvidersFrom(
        MatMenuModule,
        MatDialogModule,
        MatButtonModule,
        CommonModule,
        BrowserModule,
        Auth0Module,
        SharedModule,
        HomePageModule,
        SettingsPageModule,
        ReactiveFormsModule,
        MatTabsModule,
      ),
      AppService,
      PhenotypesService,
      PanelsService,
      CancerTypesService,
      EthnicitiesService,
      AssayService,
      EnrichmentKitsService,
      VariantEvidencesSelectionService,
      ClassificationMetadataService,
      PredictionOptionsService,
      { provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: appTooltipDefaults },
      {
        provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
        useValue: {
          subscriptSizing: 'dynamic',
        },
      },
      {
        provide: MAT_SELECT_CONFIG,
        useValue: {
          disableOptionCentering: true,
          hideSingleSelectionIndicator: true,
          panelWidth: null,
        },
      },
      provideAnimations(),
      provideHttpClient(withInterceptorsFromDi()),
    ],
  });

if (environment.hmr) {
  // tslint:disable-next-line:no-string-literal
  if (module['hot']) {
    // hmrBootstrap(module, bootstrap);
  } else {
    console.error('HMR is not enabled for webpack-dev-server!');
    console.log('Are you using the --hmr flag for ng serve?');
  }
} else {
  bootstrap().catch((err) => console.log(err));
}
